import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "../../lib/cn.js";
import { CATEGORY_CLASS, type Category } from "./category.js";
import { TINT_CLASS, type TintColor } from "./tint-tile.js";

export interface SectionHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
	/** Заголовок секции (рендерится как h2). */
	title: ReactNode;
	/** Иконка-плитка слева (опц.). */
	icon?: ReactNode;
	/** Категория плитки иконки. По умолчанию ai. */
	category?: Category;
	/**
	 * Цвет плитки-иконки как tint (перекрывает `category`). Используется для
	 * цветовой эмблемы раздела из SECTION_TINT — чтобы цвет шапки совпадал с
	 * цветом плитки инструмента на дашборде.
	 */
	tint?: TintColor;
	/** Подзаголовок / описание под заголовком (опц.). */
	description?: ReactNode;
	/** Действия справа (кнопки, ссылки, pill-ы). */
	actions?: ReactNode;
	/**
	 * На mobile (<sm) переносить блок `actions` на отдельную строку ПОД
	 * заголовком (а не ужимать title во flex-ряду). Включать, когда actions —
	 * широкий контрол (сегмент-пикер, фильтры, несколько кнопок), который на
	 * узком экране резал бы title/description. На desktop поведение не меняется.
	 */
	stackActions?: boolean;
}

/**
 * SectionHeader — единый заголовок секции «Картотека» (§3.6.10).
 * Иконка-плитка (опц.) + h2 + действия справа. Один вид во всех разделах.
 *
 * Mobile-поведение: description переносится в 2 строки (line-clamp-2), а не
 * обрезается в одну (важный подзаголовок не теряется на узком экране).
 * При `stackActions` действия уезжают под заголовок отдельной строкой на <sm.
 */
export const SectionHeader = forwardRef<HTMLDivElement, SectionHeaderProps>(
	(
		{ title, icon, category = "ai", tint, description, actions, stackActions, className, ...props },
		ref,
	) => (
		<div
			ref={ref}
			className={cn(
				stackActions
					? "flex flex-col gap-3 sm:flex-row sm:items-center"
					: "flex items-center gap-3",
				className,
			)}
			{...props}
		>
			<div className="flex min-w-0 flex-1 items-center gap-3">
				{icon && (
					<div
						className={cn(
							// tint задаёт background+color напрямую (tint-*); category — через --cb/--cf.
							tint
								? TINT_CLASS[tint]
								: cn(CATEGORY_CLASS[category], "bg-[var(--cb)] text-[var(--cf)]"),
							"inline-flex size-10 shrink-0 items-center justify-center rounded-[var(--radius-tile)] text-[20px] [box-shadow:inset_0_1px_0_rgb(255_255_255/0.4)] [&_svg]:size-5",
						)}
					>
						{icon}
					</div>
				)}
				<div className="min-w-0 flex-1">
					<h2 className="truncate text-xl font-semibold tracking-tight text-foreground">{title}</h2>
					{description && (
						<p className="line-clamp-2 text-[13px] text-muted-foreground">{description}</p>
					)}
				</div>
			</div>
			{actions && (
				<div
					className={cn(
						"flex items-center gap-2",
						stackActions ? "shrink-0 max-sm:w-full" : "shrink-0",
					)}
				>
					{actions}
				</div>
			)}
		</div>
	),
);
SectionHeader.displayName = "SectionHeader";
